// Pure helpers for the Risk tab's lightweight-charts (2026-10-23, Paolo: "the
// drawdown, sharpe and win ratio should show a better vertical scale, and the
// drawdown value should be visible on the screen"). Map DailyRiskMetricRow
// streams to {time, value} points the chart component feeds to
// lightweight-charts, and pick the default visible window. No DOM, no chart
// library — unit-tested on their own.
import type { DailyRiskMetricRow } from '../api/client'
import { streamCycleWinRate, type CycleWindow } from './riskPanelHelpers'

/** One chart point. `time` is the row's yyyy-MM-dd (a lightweight-charts business-day string). */
export interface RiskPoint { time: string; value: number }

export type RiskValueFormat = 'number2' | 'pct1' | 'pct0'

/** Ceiling on a single drawdown print. One corrupt point (an NPV=0 wipe from a
 *  scraper failure) must not rescale the chart to 120% — the source-side guard is
 *  PostReconciliationAsync's zero-wipe check; this is the chart-side belt. */
export const DD_CAP = 0.20

/** Default visible window in trading days: what the old 60-day SVG charts showed. */
export const DEFAULT_WINDOW_DAYS = 60

/** Rows sorted by date, one point per date (the last row wins), null / non-finite values dropped. */
export function metricSeries(
  rows: ReadonlyArray<DailyRiskMetricRow>,
  getter: (r: DailyRiskMetricRow) => number | null | undefined,
): RiskPoint[] {
  const byDate = new Map<string, number>()
  for (const r of [...rows].sort((a, b) => a.date.localeCompare(b.date))) {
    const v = getter(r)
    if (v === null || v === undefined || !Number.isFinite(v)) continue
    byDate.set(r.date, v)
  }
  return [...byDate.entries()].map(([time, value]) => ({ time, value }))
}

/** Drawdown as a NEGATIVE percent (0 at the top of the chart, deeper drawdown lower), capped at DD_CAP. */
export function drawdownSeries(rows: ReadonlyArray<DailyRiskMetricRow>): RiskPoint[] {
  return metricSeries(rows, r => r.currentDdPct).map(p => ({ time: p.time, value: -Math.min(p.value, DD_CAP) * 100 }))
}

/** Trade-cycle win rate over a trailing window, as a percent — the same number the hero card and
 *  Stream Breakdown show (streamCycleWinRate: the combined row weights fund + OTP cycles). */
export function winRateSeries(rows: ReadonlyArray<DailyRiskMetricRow>, window: CycleWindow = 120): RiskPoint[] {
  return metricSeries(rows, r => streamCycleWinRate(r, window).rate).map(p => ({ time: p.time, value: p.value * 100 }))
}

/** The logical range showing the last `windowDays` points (half-bar margins so the
 *  first and last points sit inside the plot). null when there is nothing to show. */
export function defaultLogicalRange(count: number, windowDays = DEFAULT_WINDOW_DAYS): { from: number; to: number } | null {
  if (count <= 0) return null
  return { from: Math.max(0, count - windowDays) - 0.5, to: count - 0.5 }
}

export function formatRiskValue(v: number, format: RiskValueFormat): string {
  if (!Number.isFinite(v)) return '—'
  switch (format) {
    case 'pct1': return v.toFixed(1) + '%'
    case 'pct0': return v.toFixed(0) + '%'
    default: return v.toFixed(2)
  }
}
